// ═══════════════════════════════════════════════════════════════════════════════
// Full-screen non-dismissible maintenance modal for user app (+ admin when closed)
// ═══════════════════════════════════════════════════════════════════════════════

import { useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { LANGUAGE, type Language } from '@fdh/shared';
import { useLanguage } from '../contexts/LanguageContext';
import { useMaintenance } from '../contexts/MaintenanceContext';
import { ADMIN_PORTAL_BASE } from '../lib/adminPortalPaths';
import { isMaintenanceBreakGlassPath } from '../lib/maintenancePaths';

type MaintenanceGateProps = {
  children: ReactNode;
};

const resolveMessage = (
  locale: Language,
  custom: { 'zh-Hant': string; 'zh-Hans': string; en: string },
  t: (key: string) => string,
): string => {
  const pick = (key: keyof typeof custom): string => {
    const value = custom[key]?.trim() ?? '';
    return value;
  };

  if (locale === LANGUAGE.ZH_HANT) {
    return pick('zh-Hant') || t('maintenance.defaultMessage');
  }
  if (locale === LANGUAGE.ZH_HANS) {
    return pick('zh-Hans') || t('maintenance.defaultMessage');
  }
  // en, id, fil → English custom or default i18n
  return pick('en') || t('maintenance.defaultMessage');
};

export const MaintenanceGate = ({ children }: MaintenanceGateProps) => {
  const { pathname } = useLocation();
  const { language, t } = useLanguage();
  const { active, message, adminPortalEnabled, isLoading } = useMaintenance();

  const isBreakGlass = isMaintenanceBreakGlassPath(pathname);
  const isAdminPortal = pathname === ADMIN_PORTAL_BASE || pathname.startsWith(`${ADMIN_PORTAL_BASE}/`);

  const shouldBlock = useMemo(() => {
    if (isLoading || !active) {
      return false;
    }
    if (isBreakGlass) {
      return false;
    }
    if (isAdminPortal && adminPortalEnabled) {
      return false;
    }
    return true;
  }, [isLoading, active, isBreakGlass, isAdminPortal, adminPortalEnabled]);

  const displayMessage = resolveMessage(language, message, t);

  return (
    <>
      {children}
      {shouldBlock ? (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-950/80 p-4 backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="maintenance-title"
          aria-describedby="maintenance-desc"
        >
          <div className="w-full max-w-md rounded-xl border border-gray-700 bg-gray-900 p-6 shadow-2xl">
            <h1 id="maintenance-title" className="text-lg font-semibold text-white">
              {t('maintenance.title')}
            </h1>
            <p id="maintenance-desc" className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-300">
              {displayMessage}
            </p>
          </div>
        </div>
      ) : null}
    </>
  );
};
